import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Clear,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSalesApi } from '../hooks/useSalesApi';
import { useProductsApi } from '../hooks/useProductsApi';
import { useClientsApi } from '../hooks/useClientsApi';
import { useAppointmentsApi } from '../hooks/useAppointmentsApi';
import { SoldProduct, Product, Client, Appointment, PaginationMeta, PaymentMethod } from '../types/entities';
import { SoldProductFormData, ProductFormData, ClientFormData } from '../types/forms';
import ProductFormModal from '../components/ProductFormModal';
import ClientFormModal from '../components/ClientFormModal';
import SellProductDialog from '../components/SellProductDialog';
import Pagination from '../components/Pagination';
import SaleDeleteConfirmationDialog from '../components/SaleDeleteConfirmationDialog';
import { usePaymentsApi } from '../hooks/usePaymentsApi';

const SalesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // API hooks
  const salesApi = useSalesApi();
  const productsApi = useProductsApi();
  const clientsApi = useClientsApi();
  const appointmentsApi = useAppointmentsApi();
  const paymentsApi = usePaymentsApi();

  // State
  const [sales, setSales] = useState<SoldProduct[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  type SortField = 'product' | 'amount' | 'price' | 'client' | 'date';
  type SortDirection = 'asc' | 'desc' | 'default';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState<SoldProduct | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<SoldProduct | null>(null);

  // Create modals state
  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [initialProductName, setInitialProductName] = useState<string>('');
  const [newlyCreatedProduct, setNewlyCreatedProduct] = useState<Product | null>(null);
  
  // Form data for create modals
  const [productFormData, setProductFormData] = useState<ProductFormData>({
    name: "",
    amount: 0,
    sell_price: "",
    description: "",
  });
  
  const [clientFormData, setClientFormData] = useState<ClientFormData>({
    name: "",
    phone_number: "",
  });


  const loadData = async (page: number = currentPage, limit: number = itemsPerPage): Promise<void> => {
    try {
      setError(null);

      // Load sales with search
      const salesResponse = await salesApi.fetchSales({
        page: page,
        limit: limit,
        search: searchTerm || undefined,
      });

      setSales(salesResponse.data);
      setPaginationMeta(salesResponse.meta);

      // Load reference data if not already loaded
      if (products.length === 0) {
        const productsResponse = await productsApi.fetchProducts({ limit: 100 });
        setProducts(productsResponse.data);
      }

      if (clients.length === 0) {
        const clientsResponse = await clientsApi.fetchClients({ limit: 10000 });
        setClients(clientsResponse.data);
      }

      if (appointments.length === 0) {
        const appointmentsResponse = await appointmentsApi.fetchAppointments({ limit: 0 }); // 0 means unlimited - get all appointments
        setAppointments(appointmentsResponse.data);
      }

    } catch (err: any) {
      setError(err.message || t('errors.fetchError'));
      toast.error(t('sales.errorLoadingSales'));
    }
  };

  const handleCreateSale = () => {
    setEditingSale(null);
    setIsModalOpen(true);
  };

  const handleEditSale = (sale: SoldProduct) => {
    setEditingSale(sale);
    setIsModalOpen(true);
  };

  const handleDeleteSale = (sale: SoldProduct) => {
    setSaleToDelete(sale);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!saleToDelete) return;
    
    try {
      await salesApi.deleteSale(saleToDelete.id);
      toast.success(t('sales.saleDeleted'));
      setDeleteDialogOpen(false);
      setSaleToDelete(null);
      loadData();
    } catch (err: any) {
      toast.error(t('sales.errorDeletingSale'));
    }
  };

  const handleSaveSale = async (formData: SoldProductFormData, selectedProducts: Array<{
    product: Product;
    amount: number;
    price: number;
  }>) => {
    try {
      const { payments, ...saleFormData } = formData;
      
      if (editingSale) {
        // Update sale
        const updatedSale = await salesApi.updateSale(editingSale.id, saleFormData);
        
        // Delete existing payments and create new ones
        if (payments && payments.length > 0) {
          // Delete old payments
          await paymentsApi.deletePaymentsBySoldProductId(editingSale.id);
          
          // Create new payments
          await Promise.all(payments.map(payment =>
            paymentsApi.createPayment({
              method: payment.method as PaymentMethod,
              amount: payment.amount,
              soldProductId: updatedSale.id,
            })
          ));
        }
        
        toast.success(t('sales.saleUpdated'));
      } else {
        // Create multiple sales for selected products
        const createdSales = await Promise.all(selectedProducts.map(productItem => 
          salesApi.createSale({
            product_id: productItem.product.id,
            client_id: formData.client_id,
            amount: productItem.amount,
            price: productItem.price,
          })
        ));
        
        // Create payments - assign each payment proportionally to each sale
        if (payments && payments.length > 0) {
          const totalPrice = selectedProducts.reduce((sum, item) => sum + (item.amount * item.price), 0);
          
          for (let i = 0; i < createdSales.length; i++) {
            const salePrice = selectedProducts[i].amount * selectedProducts[i].price;
            const paymentRatio = salePrice / totalPrice;
            
            // Calculate payments for this sale
            const salePayments = payments.map(payment => ({
              method: payment.method as PaymentMethod,
              amount: payment.amount * paymentRatio,
            }));
            
            await Promise.all(salePayments.map(payment =>
              paymentsApi.createPayment({
                method: payment.method,
                amount: payment.amount,
                soldProductId: createdSales[i].id,
              })
            ));
          }
        }
        
        toast.success(t('sales.multiSaleCreated', { count: selectedProducts.length }));
      }
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(t('sales.errorSavingSale'));
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    loadData(1, itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadData(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
    loadData(1, items);
  };

  const handleCreateProduct = async (data?: ProductFormData | Partial<ProductFormData>): Promise<void> => {
    if (!data) return;
    
    try {
      const newProduct = await productsApi.createProduct(data as ProductFormData);
      toast.success(t('products.productCreated'));
      setIsCreateProductModalOpen(false);
      // Reload products data
      const productsResponse = await productsApi.fetchProducts({ limit: 100 });
      setProducts(productsResponse.data);
      // Set the newly created product to auto-add it in SellProductDialog
      setNewlyCreatedProduct(newProduct);
      // Clear it after a moment to allow the effect to run
      setTimeout(() => setNewlyCreatedProduct(null), 100);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCreateClient = async (data?: ClientFormData | Partial<ClientFormData>): Promise<void> => {
    if (!data) return;
    
    try {
      await clientsApi.createClient(data as ClientFormData);
      toast.success(t('clients.clientCreated'));
      setIsCreateClientModalOpen(false);
      // Reload clients data
      const clientsResponse = await clientsApi.fetchClients({ limit: 10000 });
      setClients(clientsResponse.data);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Input change handlers for create modals
  const handleProductInputChange = (field: keyof ProductFormData) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    setProductFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleClientInputChange = (field: keyof ClientFormData) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    setClientFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(date));
  };

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: default -> asc -> desc -> default
      if (sortDirection === 'default') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('default');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort sales data
  const sortedSales = [...sales].sort((a, b) => {
    if (sortDirection === 'default' || !sortField) {
      return 0; // No sorting, maintain original order
    }

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'product':
        aValue = a.product.name.toLowerCase();
        bValue = b.product.name.toLowerCase();
        break;
      case 'amount':
        aValue = a.amount;
        bValue = b.amount;
        break;
      case 'price':
        aValue = a.price;
        bValue = b.price;
        break;
      case 'client':
        aValue = a.client.name.toLowerCase();
        bValue = b.client.name.toLowerCase();
        break;
      case 'date':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Debounced search effect
  useEffect(() => {
    if (!initialLoading) {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        loadData(1, itemsPerPage);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, itemsPerPage, initialLoading]);

  useEffect(() => {
    const initializeSales = async () => {
      try {
        await loadData();
      } finally {
        setInitialLoading(false);
      }
    };

    initializeSales();
  }, []);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
          {t('sales.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateSale}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('sales.addNewSale')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('sales.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          size={isMobile ? 'medium' : 'medium'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Show results or no results message */}
      {!sales || sales.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm ? t('sales.noSalesMatching', { searchTerm }) : t('sales.noSalesFound')}
          </Typography>
          {!searchTerm && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('sales.noSalesMessage')}
            </Typography>
          )}
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<Clear />}
              sx={{ mt: 2 }}
            >
              {t('sales.clearSearch')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Mobile card view */}
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sortedSales.map((sale) => (
                <Card key={sale.id} sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {sale.product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('sales.quantity')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {sale.amount}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('sales.price')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatCurrency(sale.price)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('sales.client')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {sale.client.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('common.date')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatDate(sale.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                        <IconButton
                          onClick={() => handleEditSale(sale)}
                          color="primary"
                          size="medium"
                          sx={{ 
                            padding: '8px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.25rem'
                            }
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSale(sale)}
                          color="error"
                          size="medium"
                          sx={{ 
                            padding: '8px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.25rem'
                            }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* Desktop table view */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TableSortLabel
                        active={sortField === 'product'}
                        direction={sortField === 'product' && sortDirection !== 'default' ? sortDirection : undefined}
                        onClick={() => handleSort('product')}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('sales.product')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'amount'}
                        direction={sortField === 'amount' && sortDirection !== 'default' ? sortDirection : undefined}
                        onClick={() => handleSort('amount')}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('sales.quantity')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'price'}
                        direction={sortField === 'price' && sortDirection !== 'default' ? sortDirection : undefined}
                        onClick={() => handleSort('price')}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('sales.price')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'client'}
                        direction={sortField === 'client' && sortDirection !== 'default' ? sortDirection : undefined}
                        onClick={() => handleSort('client')}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('sales.client')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'date'}
                        direction={sortField === 'date' && sortDirection !== 'default' ? sortDirection : undefined}
                        onClick={() => handleSort('date')}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('common.date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSales.map((sale) => (
                    <TableRow key={sale.id} hover>
                      <TableCell>
                        {sale.product.name}
                      </TableCell>
                      <TableCell>
                        {sale.amount}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(sale.price)}
                      </TableCell>
                      <TableCell>
                        {sale.client.name}
                      </TableCell>
                      <TableCell>{formatDate(sale.createdAt)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleEditSale(sale)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSale(sale)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Pagination */}
          {paginationMeta && (
            <Pagination
              meta={paginationMeta}
              onPageChange={handlePageChange}
              onLimitChange={handleItemsPerPageChange}
            />
          )}
        </>
      )}

      {/* Sale Form Modal */}
      <SellProductDialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewlyCreatedProduct(null);
        }}
        onSubmit={handleSaveSale}
        editingSale={editingSale}
        products={products}
        clients={clients}
        onCreateProduct={(initialName) => {
          setInitialProductName(initialName || '');
          setIsCreateProductModalOpen(true);
        }}
        onCreateClient={() => setIsCreateClientModalOpen(true)}
        newlyCreatedProduct={newlyCreatedProduct}
      />

      {/* Create Product Modal */}
      <ProductFormModal
        open={isCreateProductModalOpen}
        onClose={() => {
          setIsCreateProductModalOpen(false);
          setInitialProductName('');
        }}
        onSubmit={handleCreateProduct}
        initialName={initialProductName}
      />

      {/* Create Client Modal */}
      <ClientFormModal
        open={isCreateClientModalOpen}
        onClose={() => setIsCreateClientModalOpen(false)}
        onSubmit={handleCreateClient}
        formData={clientFormData}
        onInputChange={handleClientInputChange}
        title={t('clients.addNewClient')}
        submitButtonText={t('common.create')}
      />

      {/* Delete Sale Confirmation Dialog */}
      <SaleDeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSaleToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        sale={saleToDelete}
      />
    </Container>
  );
};

export default SalesPage;
